import { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import {
  ConversationMessageListContainer,
  DefaultConversationMessageContainer,
  defaultRenderConversationMessage,
  type RenderConversationEvent,
  type RenderConversationMessage,
} from '@/features/messages/components/ConversationMessageList/ConversationMessageListContainer'
import { ConversationLayoutContainer } from '@/features/messages/components/ConversationLayout/ConversationLayoutContainer'
import { ConversationMessageComposerContainer } from '@/features/messages/components/ConversationMessageComposer/ConversationMessageComposerContainer'
import {
  type JobMessageViewModel,
  TaxProgressionMessageViewModel,
  TaxAssistantMessageViewModel,
  type TaxJobConversationEvent,
} from '@/features/jobs/details/tax-type/TaxJobConversationViewModel.ts'
import {
  ConversationMessage,
  ConversationMessageCardContent,
  ConversationMessageCardContentButton,
  ConversationMessageCardContentStatus,
  ConversationMessageListTimelineEvent,
  type ConversationMessageProps,
} from '@/features/messages/components/ConversationMessageList'
import taxAssistantAvatarUrl from '@/assets/avatars/avatar-tax-assistant.svg'
import { match } from '@/utils/pattern-match-util'
import { dateFormatters } from '@/utils/date-util'
import { toStream } from '@/utils/mobx-util'
import {
  animationFrameScheduler,
  filter,
  map,
  type Observable,
  observeOn,
} from 'rxjs'
import type {
  ConversationLayoutProps,
  ScrollToBottomOptions,
} from '@/features/messages/components/ConversationLayout'

interface TaxJobConversationContainerProps {
  taxVm: TaxJobDetailsViewModel

  /** @see ConversationLayoutProps.scrollingElement */
  scrollingElement?: HTMLElement | null

  /** @see ConversationLayoutProps.composerPositioning */
  composerPositioning?: ConversationLayoutProps['composerPositioning']

  /** @see ConversationLayoutProps.isComposerHidden */
  isComposerHidden?: ConversationLayoutProps['isComposerHidden']

  /** @see ConversationLayoutProps.composerMask */
  composerMask?: ConversationLayoutProps['composerMask']

  /** @see ConversationLayoutProps.spacing */
  spacing?: ConversationLayoutProps['spacing']

  /**
   * Pass this to only start scrolling once this emits `true`.
   */
  isReadyToScroll$?: Observable<boolean>
}

/**
 * Tax job specific conversation layout container.
 */
export const TaxJobConversationContainer = observer(
  function TaxJobConversationContainer({
    taxVm,
    ...rest
  }: TaxJobConversationContainerProps) {
    const renderConversationMessage: RenderConversationMessage<JobMessageViewModel> =
      useCallback((message, props) => {
        switch (true) {
          case message instanceof TaxAssistantMessageViewModel: {
            return (
              <TaxAssistantConversationMessageContainer
                id={message.messageVm.key}
                key={message.messageVm.key}
                vm={message}
                {...props}
              />
            )
          }

          case message instanceof TaxProgressionMessageViewModel: {
            return (
              <TaxProgressionConversationMessageContainer
                id={message.messageVm.key}
                key={message.messageVm.key}
                vm={message}
                {...props}
              />
            )
          }
        }

        return defaultRenderConversationMessage(message.messageVm, props)
      }, [])

    const renderEvent: RenderConversationEvent<TaxJobConversationEvent> =
      useCallback((event) => {
        switch (event.type) {
          case 'TaxAssistantLeft': {
            return (
              <ConversationMessageListTimelineEvent
                label="The Tax Assistant has left the chat"
                key="tax-assistant-left"
                id="tax-assistant-left"
              />
            )
          }
        }
      }, [])

    // Since we're loading extra stuff for the tax VM, we'll scroll to the bottom
    // once it's all been loaded.
    const scrollToBottom$ = useMemo(
      () =>
        toStream(() => taxVm.jobConversationVm.fetch.resolved, true).pipe(
          filter(Boolean),
          map((): ScrollToBottomOptions => ({ behavior: 'instant' })),
          observeOn(animationFrameScheduler),
        ),
      [taxVm],
    )

    const isComposerVisible =
      taxVm.job.status !== 'Closed' && !taxVm.jobConversationVm.fetch.pending
    return (
      <ConversationLayoutContainer
        {...rest}
        typing={taxVm.jobConversationVm.typingNames}
        conversationVm={taxVm.jobConversationVm.conversationVm}
        scrollToBottom$={scrollToBottom$}
        chatVm={taxVm.jobConversationVm.chatVm}
        composer={
          isComposerVisible && (
            <ConversationMessageComposerContainer
              vm={taxVm.jobConversationVm.conversationVm}
              suggestions={taxVm.jobConversationVm.suggestions}
              testId="job-messages-composer"
              textareaTestId="job-messages-composer-textarea"
              sendButtonTestId="job-messages-composer-send-button"
            />
          )
        }
        messageList={
          <ConversationMessageListContainer
            aria-label="Conversation"
            isPending={taxVm.jobConversationVm.fetch.pending}
            chatListVm={taxVm.jobConversationVm.chatVm}
            conversationVm={taxVm.jobConversationVm.conversationVm}
            renderConversationMessage={renderConversationMessage}
            renderEvent={renderEvent}
            testId="job-messages-list"
          />
        }
      />
    )
  },
)

interface TaxAssistantConversationMessageContainerProps
  extends ConversationMessageProps {
  vm: TaxAssistantMessageViewModel
}

/**
 * Container for a tax assistant message.
 */
const TaxAssistantConversationMessageContainer = observer(
  function TaxAssistantConversationMessageContainer({
    vm,
    ...props
  }: TaxAssistantConversationMessageContainerProps) {
    return (
      <DefaultConversationMessageContainer
        vm={vm.messageVm}
        {...props}
        avatarUrl={taxAssistantAvatarUrl}
        displayName="Tax Assistant"
      />
    )
  },
)

interface TaxProgressionConversationMessageContainerProps
  extends ConversationMessageProps {
  vm: TaxProgressionMessageViewModel
}

/**
 * Container for a tax progression message (job status update cards).
 */
const TaxProgressionConversationMessageContainer = observer(
  function TaxProgressionConversationMessageContainer({
    vm,
    ...props
  }: TaxProgressionConversationMessageContainerProps) {
    if (!vm.step) {
      return (
        <ConversationMessage {...props} isSkeleton>
          <ConversationMessageCardContent
            title="Placeholder"
            description="Placeholder"
          />
        </ConversationMessage>
      )
    }

    return (
      <ConversationMessage
        {...props}
        testId={`message-progression-${vm.step.step}`}
      >
        <ConversationMessageCardContent
          title={vm.step.content.title}
          description={vm.step.content.description}
        >
          {match(vm.step, 'step', {
            DraftInReview: (inReview) =>
              !inReview.completedAt && (
                <ConversationMessageCardContentButton
                  label="Review your draft"
                  onPress={vm.viewLatestDraft}
                />
              ),
            DraftRejected: (rejected) => (
              <ConversationMessageCardContentStatus>
                You requested changes on{' '}
                {dateFormatters.dateTimeFull.format(rejected.completedAt)}
              </ConversationMessageCardContentStatus>
            ),
            DraftApproved: (approved) => (
              <ConversationMessageCardContentStatus>
                You approved the draft on{' '}
                {dateFormatters.dateTimeFull.format(approved.completedAt)}
              </ConversationMessageCardContentStatus>
            ),
            Other: () => null,
          })}
        </ConversationMessageCardContent>
      </ConversationMessage>
    )
  },
)
